import { queryKeys } from "@/api/hooks";
import { QueryErrorState } from "@/api/QueryErrorState";
import { QueryPendingState } from "@/api/QueryPendingState";
import { useAssetVersionQuery } from "@/api/library";
import {
  NO_VALIDITY_SENTENCE,
  isQuestionnaireContent,
  itemCountSentence,
  scoringSentence,
  severityBandSentence,
  validityStateLabel,
  validityThresholdSentence,
} from "@/lib/questionnaire";
import { Badge } from "@/ui/Badge";

import type { PinConsequenceProps } from "./consequences";

/**
 * What pinning a questionnaire does to a project (#370).
 *
 * The seam's second registered renderer, and the Summary counterpart of the
 * adoption row #369 built: `/adoptions` already told a clinician what their
 * pinned instrument administers, and the Summary — the screen that answers
 * "what does this project's configuration actually do" — answered "not rendered
 * here yet" for the one asset type the epic is named after.
 *
 * WHERE THE ANSWER COMES FROM. The pin says `assetId` and `version`; how many
 * items that version administers, whether it is scored, how many bands stratify
 * the score and whether its validity screen fires are in its stored content and
 * nowhere else. The contract serves content only at an explicit version — there
 * is no "content at latest" read in it — so this reads exactly the version the
 * project pinned. `asset.latestVersion` sources not one number here; it decides
 * only whether the block has to say that the library has moved on.
 *
 * NOTHING IS COMPUTED. Every sentence below is `lib/questionnaire.ts`'s
 * read-only vocabulary applied to the document the Editor Service returned. No
 * score is calculated, no respondent is scored, no band membership is resolved,
 * and no count is inferred from a value: which band a fractional mean resolves
 * to is a runtime mapping, and this surface reports what the instrument stores
 * rather than what any answer to it would produce.
 *
 * WHY THE VOCABULARY IS IMPORTED RATHER THAN WRITTEN. Three surfaces now
 * describe one instrument — the library's version viewer, the Project's
 * adoption row, and this — and `lib/questionnaire.ts` exists because a clinician
 * "must not be told the same fact in two different sentences". A renderer that
 * re-worded "Scored — total of item responses" locally would reintroduce exactly
 * the drift #368 and #369 removed.
 *
 * AN UNREAD VERSION IS UNREAD, NOT AN EMPTY INSTRUMENT. The pending branch goes
 * through `QueryPendingState` rather than a bare skeleton, so a held retry
 * renders as the connection or as the failure the service already answered
 * (#215, refined by #327) instead of a spinner that never resolves; the settled
 * branch goes through `QueryErrorState`, so an expired sign-in, a denial and an
 * unimplemented endpoint each keep their own recovery (#4, #44). None of them
 * ever renders as an instrument with no items, no bands and no validity screen.
 *
 * WHAT IT DOES NOT DO. It holds no adoption state — the pin arrives as a prop
 * from the Summary's single `useProjectAdoptionsQuery` — and it renders no
 * control that writes. Adoption, upgrade, diff and safety-lock controls are
 * #372's surface.
 */
export function QuestionnaireConsequence({ pin, asset }: PinConsequenceProps) {
  const version = useAssetVersionQuery(pin.assetId, pin.version);

  if (version.isPending) {
    return (
      <QueryPendingState
        isPaused={version.isPaused}
        failureReason={version.failureReason}
        label={`Reading what ${asset.name} ${pin.version} administers`}
        // The error branch's own title, verbatim: a held retry becomes that
        // failure once the retries are spent, and two headings for one failure
        // would read as two different problems.
        errorTitle={FAILURE_TITLE}
        rows={2}
        queryKey={queryKeys.assetVersion(pin.assetId, pin.version)}
      />
    );
  }

  if (version.isError) {
    return (
      <QueryErrorState
        error={version.error}
        subject="Questionnaire consequences"
        title={FAILURE_TITLE}
        // Stated rather than glossed: the pin itself is known and rendered
        // above this block, so the honest failure is "the pinned version's
        // content did not load", not "this instrument carries nothing".
        description={`This project pins ${asset.name} at ${pin.version}. The content of that version could not be read, so what it administers — its items, its scoring, its severity bands and its validity screen — is not shown. None of them is a statement that it carries none.`}
        onRetry={() => void version.refetch()}
      />
    );
  }

  const { content } = version.data;
  if (!isQuestionnaireContent(content)) {
    // The catalog said `questionnaire` and the version detail disagreed. Two
    // server-owned facts that cannot both be right, so neither is presented as
    // if it were: nothing is rendered from the content, and the disagreement is
    // what the clinician is told.
    return (
      <p role="alert" className="text-sm text-destructive">
        {asset.name} {pin.version} is catalogued as a questionnaire but its
        stored content is not one, so nothing here describes what it
        administers.
      </p>
    );
  }

  return (
    <div className="space-y-3 text-sm">
      <p className="text-foreground">
        This project administers <strong>{asset.name}</strong> at {pin.version}.
        That version is immutable, so publishing a newer one does not move this
        pin.
      </p>

      {/*
        The three facts as the shared vocabulary states them, not as numbers
        with captions written here: each string is already a whole statement —
        "3 items", "Not scored", "No severity bands" — and an unbanded or
        unscored instrument is a real configuration rather than a missing value.
      */}
      <ul className="grid gap-2 sm:grid-cols-3">
        <li className="rounded-md border border-border px-3 py-2 text-foreground">
          {itemCountSentence(content)}
        </li>
        <li className="rounded-md border border-border px-3 py-2 text-foreground">
          {scoringSentence(content)}
        </li>
        <li className="rounded-md border border-border px-3 py-2 text-foreground">
          {severityBandSentence(content)}
        </li>
      </ul>

      <div className="space-y-1.5">
        <p className="font-medium text-foreground">Validity screen</p>
        {content.validity ? (
          <p className="flex flex-wrap items-center gap-2 text-muted-foreground">
            <Badge tone="outline">{validityStateLabel(content.validity)}</Badge>
            <span>{validityThresholdSentence(content.validity)}</span>
          </p>
        ) : (
          // Said, never omitted. An instrument with no screen, a screen that is
          // stored and switched off, and a screen whose version could not be
          // read are three different clinical situations, and a silent block
          // renders them identically.
          <p className="text-muted-foreground">{NO_VALIDITY_SENTENCE}</p>
        )}
        <p className="text-xs text-muted-foreground">
          The screen belongs to this instrument version: a project adopts it with
          the pin and cannot re-point it. Its indicator items are summed into the
          one total the threshold is checked against, so they are counted rather
          than listed here.
        </p>
      </div>

      {pin.version !== asset.latestVersion ? (
        // The pin is immutable and the library has moved past it. Saying so
        // HERE, beside the counts, is what stops the card header's own "Library
        // head is v2" badge from being read as a description of the numbers
        // above it. The sentence is the adoption row's word for word, because
        // it is the same fact and a clinician reading both screens must not
        // meet two versions of it.
        <p className="text-xs text-muted-foreground">
          The shared library holds a newer version; nothing above describes it.
        </p>
      ) : null}
    </div>
  );
}

/**
 * One heading for one failure, shared by the pending and settled branches.
 *
 * `QueryPendingState` documents why it has to be the same string: a paused read
 * is the error branch's failure, still holding, and the clinician watches one
 * screen turn into the other.
 */
const FAILURE_TITLE = "What this pin administers could not be loaded";
